
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/auth";
import { supabase } from "@/integrations/supabase/client";
import { getFunctionsUrl } from "@/utils/supabaseConfig";
import { toast } from "sonner";

interface User {
  id: string;
  full_name: string | null;
  is_premium: boolean;
  premium_since: string | null;
}

export const useAdminUsers = () => {
  const { user, refreshPremiumStatus } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    
    // Check if the current user is authorized to access admin page
    const checkAuthorization = async () => {
      const authorizedEmail = "satoshi.0921@gmail.com";
      console.log("Current user email:", user.email);
      console.log("Authorized email:", authorizedEmail);
      
      // Check if emails match (case insensitive)
      const userIsAuthorized = user.email?.toLowerCase() === authorizedEmail.toLowerCase();
      console.log("Is user authorized:", userIsAuthorized);
      
      setIsAuthorized(userIsAuthorized);
      
      if (!userIsAuthorized) {
        toast.error("You don't have permission to access the admin page");
        navigate("/");
        return false;
      }
      
      return true;
    };
    
    const initAdmin = async () => {
      const isAuth = await checkAuthorization();
      if (isAuth) {
        fetchUsers();
      }
    };
    
    initAdmin();
  }, [user, navigate]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('No active session found');
      }

      const baseUrl = getFunctionsUrl();
      const response = await fetch(`${baseUrl}/admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`,
        },
        body: JSON.stringify({ action: 'get-users' }),
      });
      
      const result = await response.json();
      
      if (result.error) throw new Error(result.error);
      
      if (result.data && Array.isArray(result.data.users)) {
        setUsers(result.data.users || []);
      } else {
        console.error('Unexpected response format:', result);
        toast.error('Failed to load users. Unexpected response format.');
        setUsers([]);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users. Please try again later.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const togglePremium = async (userId: string, isPremium: boolean) => {
    setUpdating(userId);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error('No active session found');
      }

      const baseUrl = getFunctionsUrl();
      const response = await fetch(`${baseUrl}/admin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionData.session.access_token}`,
        },
        body: JSON.stringify({ 
          action: 'toggle-premium', 
          userId, 
          isPremium 
        }),
      });
      
      const result = await response.json();
      
      if (result.error) throw new Error(result.error);
      
      // Update local user list
      setUsers(users.map(u => 
        u.id === userId 
          ? { ...u, is_premium: isPremium, premium_since: isPremium ? new Date().toISOString() : null }
          : u
      ));
      
      toast.success(`User premium status updated to ${isPremium ? 'Premium' : 'Free'}`);
      
      // If the current user was updated, refresh their premium status
      if (userId === user?.id) {
        await refreshPremiumStatus();
      }
    } catch (error) {
      console.error('Error toggling premium status:', error);
      toast.error('Failed to update premium status. Please try again.');
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const getCurrentUserIsPremium = () => {
    const currentUser = users.find(u => u.id === user?.id);
    return currentUser?.is_premium || false;
  };

  return {
    users,
    loading,
    updating,
    isAuthorized,
    togglePremium,
    formatDate,
    getCurrentUserIsPremium,
    fetchUsers  // Added fetch function for manual refresh
  };
};
